// import { useLocalSearchParams, router, useNavigation } from 'expo-router';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import React, { useCallback, useEffect, useRef, useState } from 'react';
// import { Alert, ScrollView } from 'react-native';

import { CobuAlert } from '@cobu/ui';
import { useTheme, Button } from 'tamagui';

import {
  CobuContainer,
  CobuText,
  GroupDetailSkeleton,
  CobuHeader,
  CobuBackButton,
} from '@cobu/ui';
// import { GroupInfoDetails } from '../../components/groups/GroupInfoDetails';
import { Strings } from '../../constants';
import { GroupInfoDetails } from '../../components/groups/GroupInfoDetails';
import { useResidentRestrictions } from '../../hooks/useResidentRestrictions';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
// import eventsService from '@/services/events/eventsService';
import { useGroupDetail } from '../../hooks/groups/useGroupDetail';
// import { useNavigate, useParams } from 'react-router-dom';
import { showErrorMessage } from '../../utils/messageToast';
import { logger } from '../../utils/logger';

dayjs.extend(utc);
dayjs.extend(timezone);

export default function GroupInfoScreen() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const stateParams = (location.state ?? undefined) as {
    id?: string;
    members?: any[];
    group?: any;
    buildingName?: string;
    currentUser?: any;
    currentGroupEvents?: any[];
    userIsGroupMember?: boolean;
  } | undefined;
  const params = {
    id: stateParams?.id ?? (searchParams.get('id') ?? ''),
    members: stateParams?.members ?? (searchParams.get('members') ?? ''),
    group: stateParams?.group ?? (searchParams.get('group') ?? ''),
    buildingName: stateParams?.buildingName ?? (searchParams.get('buildingName') ?? ''),
    currentUser: stateParams?.currentUser ?? (searchParams.get('currentUser') ?? ''),
    currentGroupEvents: stateParams?.currentGroupEvents ?? (searchParams.get('currentGroupEvents') ?? ''),
    userIsGroupMember: typeof stateParams?.userIsGroupMember === 'boolean'
      ? String(stateParams?.userIsGroupMember)
      : (searchParams.get('userIsGroupMember') ?? ''),
  };
  const navigate = useNavigate();
  const theme = useTheme();
  const strings = Strings.get();

  // Parse the route params (prefer state; fall back to query strings)
  const group = typeof params.group === 'object' && params.group !== null
    ? params.group
    : (typeof params.group === 'string' && params.group ? JSON.parse(params.group) : null);
  const members = Array.isArray(params.members)
    ? params.members
    : (typeof params.members === 'string' && params.members ? JSON.parse(params.members) : []);
  const gatherings = Array.isArray(params.currentGroupEvents)
    ? params.currentGroupEvents
    : (typeof params.currentGroupEvents === 'string' && params.currentGroupEvents ? JSON.parse(params.currentGroupEvents) : []);
  const userIsGroupMember = params.userIsGroupMember === 'true';

  const {
    group: hookGroup,
    gatherings: hookGatherings,
    members: hookMembers,
    userIsGroupMember: hookUserIsGroupMember,
    handleLeaveGroup,
    isLeaving,
    handleJoinGroup,
    isJoining,
    isLoading,
    error,
  } = useGroupDetail(params.id, true);
  
  const { canJoinGroup } = useResidentRestrictions();
  const currentGroup = hookGroup || group;
  const isBWGroup = !!currentGroup && (currentGroup.isDefault || currentGroup.isMarketplace);
  
  // Use hook data if available, otherwise fall back to params
  const userIsGroupMemberFinal = hookGroup ? hookUserIsGroupMember : userIsGroupMember;
  
  // Alert states
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [showLeaveError, setShowLeaveError] = useState(false);

  const handleJoinGroupPress = async () => {
    const currentGroup = hookGroup || group;
    if (!currentGroup) return;
    try {
      await handleJoinGroup();
      navigate(-1);
    } catch (error) {
      logger.error('Failed to join group:', error);
    }
  };

  const getUpcomingRsvps = (
    gatherings: Array<{ uuid: string; scheduledStartTime?: string; rsvpId?: string; buildingTimezone?: string | null }>
  ) => {
    try {
      return gatherings
        .filter(g => !!g.rsvpId && !!g.scheduledStartTime && dayjs(g.scheduledStartTime).isAfter(dayjs()))
        .map(g => ({
          rsvpId: g.rsvpId as string,
          gatheringId: g.uuid,
          interestedDate: g?.scheduledStartTime
            ? (g?.buildingTimezone
              ? dayjs(g.scheduledStartTime).tz(g.buildingTimezone).format('YYYY-MM-DD')
              : dayjs(g.scheduledStartTime).format('YYYY-MM-DD'))
            : undefined,
        }));
    } catch {
      return [];
    }
  };

  const cancelRsvps = async (
    rsvps: Array<{ rsvpId: string }>,
    deleteFn: (id: string) => Promise<unknown>
  ) => {
    if (!rsvps.length) return;
    await Promise.all(rsvps.map(({ rsvpId }) => deleteFn(rsvpId)));
  };

  const proceedWithLeaveGroup = useCallback(async () => {
    try {
      // await cancelRsvps(upcomingRsvps, eventsService.deleteGatheringRSVP);
      await handleLeaveGroup();
      navigate(-1);
    } catch (error) {
      logger.error('Failed to leave group:', error);
      setShowLeaveError(true);
    }
  }, [handleLeaveGroup, navigate]);

  const handleLeaveGroupPress = useCallback(() => {
    const currentGroup = hookGroup || group;
    if (!currentGroup) return;

    const currentGatherings = (hookGatherings && hookGatherings.length > 0) ? hookGatherings : gatherings;
    const upcomingRsvps = getUpcomingRsvps(currentGatherings);

    // Show confirmation if there are upcoming RSVPs, otherwise proceed directly
    if (upcomingRsvps.length) {
      setShowLeaveConfirm(true);
    } else {
      // Inline the leave logic to avoid dependency issues
      (async () => {
        try {
          await handleLeaveGroup();
          navigate(-1);
        } catch (error) {
          logger.error('Failed to leave group:', error);
          setShowLeaveError(true);
        }
      })();
    }
  }, [hookGroup, group, hookGatherings, gatherings, handleLeaveGroup, navigate]);

  const renderHeader = () => (
    <CobuHeader
      title={strings.groups.info.title}
      renderLeft={() => <CobuBackButton onPress={() => navigate(-1)} />}
      renderRight={() => {
        // Wait for hook to finish loading if we're fetching data
        if (params.id && isLoading && !hookGroup) return null;
        
        if (!currentGroup) return null;
        if (isBWGroup) return null;
        
        const isMember = userIsGroupMemberFinal;
        if (isMember) {
          return (
            <Button
              backgroundColor="$buttonTertiaryBackground"
              px="$4"
              h="$buttonTertiaryHeight"
              pressStyle={{ 
                backgroundColor: '$buttonTertiaryBackground',
              }}
              hoverStyle={{ 
                backgroundColor: '$buttonTertiaryBackground',
              }}
              focusStyle={{ 
                backgroundColor: '$buttonTertiaryBackground',
              }}
              disabledStyle={{
                backgroundColor: '$buttonTertiaryBackground',
              }}
              onPress={handleLeaveGroupPress}
              disabled={isLeaving}
              style={{ marginRight: 12 }}
            >
              <CobuText variant="body" color="$buttonTertiaryText" >
                {isLeaving ? strings.groups.info.buttons.leaving : strings.groups.info.buttons.leave}
              </CobuText>
            </Button>
          );
        }
        if (!canJoinGroup) return null;
        return (
          <Button
            h="$buttonTertiaryHeight"
            backgroundColor="$buttonTertiaryBackground"
            px="$4"
            pressStyle={{ 
              backgroundColor: '$buttonTertiaryBackground',
              opacity: 0.9,
            }}
            hoverStyle={{ 
              backgroundColor: '$buttonTertiaryBackground',
              opacity: 0.8,
            }}
            focusStyle={{ 
              backgroundColor: '$buttonTertiaryBackground',
              opacity: 1,
            }}
            disabledStyle={{
              backgroundColor: '$buttonTertiaryBackground',
              opacity: 0.6,
            }}
            onPress={handleJoinGroupPress}
            disabled={isJoining}
          >
            <CobuText variant="body" color="$buttonTertiaryText" >
              {isJoining ? strings.groups.info.buttons.joining : strings.groups.info.buttons.join}
            </CobuText>
          </Button>
        );
      }}
    />
  );

  // Error state
  const hasShownErrorRef = useRef(false);
  useEffect(() => {
    if (error && !hasShownErrorRef.current) {
      showErrorMessage(strings.common.somethingWentWrong);
      hasShownErrorRef.current = true;
    }
  }, [error]);
  // Loading state
  if (isLoading) {
    return (
      <CobuContainer bg="$white">
        <GroupDetailSkeleton />
      </CobuContainer>
    );
  }

  // Error state
  if (error || !(group || hookGroup)) {
    return null;
  }

  return (
    <CobuContainer bg="$white">
        {renderHeader()}
        <GroupInfoDetails
          group={hookGroup || group}
          gatherings={(hookGatherings && hookGatherings.length > 0) ? hookGatherings as any : gatherings as any}
          members={(hookMembers && hookMembers.length > 0) ? hookMembers as any : members as any}
          userIsGroupMember={userIsGroupMemberFinal}
        />
        
        {/* Leave confirmation alert */}
        <CobuAlert
          open={showLeaveConfirm}
          onOpenChange={setShowLeaveConfirm}
          title={strings.groups.info.leaveConfirm.title}
          description={strings.groups.info.leaveConfirm.message}
          acceptText={strings.common.buttonLabels.yes}
          cancelText={strings.common.buttonLabels.no}
          onAccept={() => {
            setShowLeaveConfirm(false);
            proceedWithLeaveGroup();
          }}
          onCancel={() => setShowLeaveConfirm(false)}
        />
        
        {/* Leave error alert */}
        <CobuAlert
          open={showLeaveError}
          onOpenChange={setShowLeaveError}
          title={strings.common.error.title}
          description={strings.groups.info.errors.leaveFailed}
          acceptText={strings.common.buttonLabels.ok}
          onAccept={() => setShowLeaveError(false)}
        />
    </CobuContainer>
  );
} 